<template>
  <v-card min-height="100%">
    <v-card-title class="pa-2 subtitle-1 pb-0">
      <b>Heatmap</b>
      <v-spacer />
      <v-tooltip left>
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            small
            icon
            :color="sync ? 'primary': null"
            v-bind="attrs"
            v-on="on"
            @click="syncFilters = !syncFilters"
          >
            <v-icon
              small
            >
              {{ syncFilters ? mdiSync : mdiSyncOff }}
            </v-icon>
          </v-btn>
        </template>
        <span>{{ syncFilters ? "Filters are synchronized" : "Filters are not synchronized" }}</span>
      </v-tooltip>
    </v-card-title>
    <v-card-text class="pa-2 ma-0">
      <session-filters
        ref="heatmapFilters"
        :map-id="mapId"
        :filtersValue="selectedHeatmapsData"
        :use-narration-nodes="true"
        :use-session-min-time="true"
        :update-narration-nodes-list="['build_id', 'user_id', 'session_id']"
        @update="updateHeatmapsFilters"
      />
      <difficulty-select
        :value="selectedHeatmapsData.difficulty"
        @update="(level) => updateHeatmapsFilters({ difficulty: level })"
      />
      <v-divider class="mt-2 mb-2"/>
      <v-checkbox
        v-for="heatmap in heatmaps"
        :key="heatmap.id"
        class="mt-0"
        :label="heatmap.name"
        hide-details
        dense
        @change="(event) => toggleHeatmap(event, heatmap)"
      />
    </v-card-text>
  </v-card>
</template>

<script>
import { mdiSync, mdiSyncOff } from '@mdi/js'
import { HeatmapStatisticLegend } from '../../../plugins/leaflet-legends'
import { setPopupWrapper, inside } from '../../../plugins/helpers/map-helpers'
import HeatmapLegend from '../../../plugins/leaflet-legends/heatmap-legend'
import SessionFilters from '../../filters/Telemetry/SessionFilters.vue'
import DifficultySelect from '../../filters/Telemetry/DifficultySelect.vue'
import L from 'leaflet'

export default {
  props: {
    map: {
      type: Object,
      required: true
    },
    sync: {
      type: Boolean,
      required: true
    }
  },
  components: {
    SessionFilters,
    DifficultySelect
  },
  data () {
    return {
      mdiSync,
      mdiSyncOff,
      heatmaps: [],
      mapId: this.map.options.currentMapId,
      selectedHeatmapsData: {
        build_id: this.$store.state.telemetry.build_id,
        user_id: this.$store.state.telemetry.user_id,
        session_id: this.$store.state.telemetry.session_id,
        start_narration_node: this.$store.state.telemetry.start_narration_node,
        finish_narration_node: this.$store.state.telemetry.finish_narration_node,
        session_min_time: '00:00:00',
        difficulty: -1
      },
      statisticLegend: null,
      heatmapLegend: new HeatmapLegend({
        vue: this
      }),
      setPopupWrapper: setPopupWrapper.bind(this)
    }
  },
  deactivated () {
    if (this.statisticLegend) {
      this.statisticLegend.remove()
      this.statisticLegend = null
    }
  },
  async mounted () {
    await Promise.all([
      this.getPlaytimeStatistic(),
      this.getHeatmapsList()
    ])

    this.map.on('change-map-id', this.changeMapId)
    this.map.on('change-map', this.updateHeatmaps)
    this.map.on('change-type', this.updateHeatmaps)
    this.map.on('update-heatmap-canvas', this.updateHeatmapCanvas)
  },
  beforeDestroy () {
    this.map.off('change-map-id', this.changeMapId)
    this.map.off('change-map', this.updateHeatmaps)
    this.map.off('change-type', this.updateHeatmaps)
    this.map.off('update-heatmap-canvas', this.updateHeatmapCanvas)
  },
  computed: {
    syncFilters: {
      get () {
        return this.sync
      },
      set (val) {
        this.$emit('set-sync', [val, 'Heatmaps', 'Timeline'])
      }
    }
  },
  watch: {
    async 'selectedHeatmapsData.build_id' () {
      await this.getPlaytimeStatistic()
      this.updateHeatmaps()
    },
    async 'selectedHeatmapsData.user_id' () {
      await this.getPlaytimeStatistic()
      this.updateHeatmaps()
    },
    async 'selectedHeatmapsData.session_id' () {
      await this.getPlaytimeStatistic()
      this.updateHeatmaps()
    },
    async 'selectedHeatmapsData.start_narration_node' () {
      await this.getPlaytimeStatistic()
      this.updateHeatmaps()
    },
    async 'selectedHeatmapsData.finish_narration_node' () {
      await this.getPlaytimeStatistic()
      this.updateHeatmaps()
    },
    async 'selectedHeatmapsData.session_min_time' () {
      await this.getPlaytimeStatistic()
      this.updateHeatmaps()
    },
    async 'selectedHeatmapsData.difficulty' () {
      await this.getPlaytimeStatistic()
      this.updateHeatmaps()
    }
  },
  methods: {
    updateHeatmapsFilters (data) {
      this.selectedHeatmapsData = { ...this.selectedHeatmapsData, ...data }
      if (this.sync) {
        this.$emit('sync-filters', [{ ...this.selectedTimelineData }, 'Timeline'])
      }
    },
    async getHeatmapsList () {
      const { data } = await this.$axios.get(this.$store.getters.projectPath + '/configs/telemetry/heatmaps')
      .catch(error => ({ data: { err: true, message: error.response.data } }))

      if (data.err) {
        this.$toasted.error(data.message)
      } else {
        this.heatmaps = data
      }
    },
    async getPlaytimeStatistic () {
      if (!this.selectedHeatmapsData.build_id) return

      const { data } = await this.$axios.get(
        `${this.$store.getters.projectPath}/telemetry/playtime/statistic`, {
          params: {
            build_id: this.selectedHeatmapsData.build_id,
            user_id: this.selectedHeatmapsData.user_id,
            session_id: this.selectedHeatmapsData.session_id,
            start_narration_node: this.selectedHeatmapsData.start_narration_node,
            finish_narration_node: this.selectedHeatmapsData.finish_narration_node,
            session_min_time: this.selectedHeatmapsData.session_min_time,
            difficulty: this.selectedHeatmapsData.difficulty
          }
        }
      ).catch(error => ({ data: { err: true, message: error.response.data } }))

      if (data.err) {
        this.$toasted.error(data.message)
      } else if (this.statisticLegend) {
        this.statisticLegend.updateStatisticFilters({
          map_id: this.map.options.currentMapId,
          build_id: this.selectedHeatmapsData.build_id,
          user_id: this.selectedHeatmapsData.user_id,
          session_id: this.selectedHeatmapsData.session_id,
          start_narration_node: this.selectedHeatmapsData.start_narration_node,
          finish_narration_node: this.selectedHeatmapsData.finish_narration_node,
          session_min_time: this.selectedHeatmapsData.session_min_time,
          difficulty: this.selectedHeatmapsData.difficulty
        })
        this.statisticLegend.updateStatisticData(data, this.selectedHeatmapsData.start_narration_node &&  this.selectedHeatmapsData.finish_narration_node)
      } else {
        this.statisticLegend = new HeatmapStatisticLegend({
          vue: this,
          statisticData: data,
          statisticFilters: {
            map_id: this.map.options.currentMapId,
            build_id: this.selectedHeatmapsData.build_id,
            user_id: this.selectedHeatmapsData.user_id,
            session_id: this.selectedHeatmapsData.session_id,
            start_narration_node: this.selectedHeatmapsData.start_narration_node,
            finish_narration_node: this.selectedHeatmapsData.finish_narration_node,
            session_min_time: this.selectedHeatmapsData.session_min_time,
            difficulty: this.selectedHeatmapsData.difficulty
          }
        })
        this.statisticLegend.addTo(this.map)
      }
    },
    async changeMapId () {
      this.mapId = this.map.options.currentMapId
      this.selectedHeatmapsData = {
        build_id: null,
        user_id: null,
        session_id: null,
        start_narration_node: null,
        finish_narration_node: null,
        session_min_time: this.selectedHeatmapsData.session_min_time,
        difficulty: this.selectedHeatmapsData.difficulty
      }
    },
    async toggleHeatmap (open, heatmap) {
      heatmap.active = open
      if (open) {
        if (!this.heatmapLegend.options.show) {
          this.heatmapLegend.addTo(this.map)
        }
        await this.loadHeatmap(heatmap)
      } else if (heatmap.canvas) {
        this.heatmapLegend.removeHeatmap({ id: heatmap.id })
        heatmap.canvas.remove()
        heatmap.canvas = null
      } else if (heatmap.group) {
        heatmap.group.remove()
        heatmap.group = null
      }
    },
    updateHeatmaps () {
      this.heatmaps.forEach(async heatmap => {
        if (heatmap.active) {
          await this.loadHeatmap(heatmap)
        }
      })
    },
    updateHeatmapCanvas ({ number, heatmap }) {
      const data = this.heatmaps.find(val => val.id === heatmap.id)
      if (data && data.canvas) {
        data.canvas.setOptions({
          max: number
        })
      }
    },
    async loadHeatmap (heatmap) {
      const { data } = await this.$axios.get(this.$store.getters.projectPath + '/map/telemetry/heatmap/' + heatmap.id, {
        params: {
          build_id: this.selectedHeatmapsData.build_id,
          user_id: this.selectedHeatmapsData.user_id,
          session_id: this.selectedHeatmapsData.session_id,
          narration_nodes: [this.selectedHeatmapsData.start_narration_node, this.selectedHeatmapsData.finish_narration_node],
          map_name: this.map.options.currentMap,
          map_floor: this.map.options.currentType,
          session_min_time: this.selectedHeatmapsData.session_min_time,
          difficulty: this.selectedHeatmapsData.difficulty
        }
      })
      .catch(error => ({ data: { err: true, message: error.response.data } }))

      if (data.err) {
        this.$toasted.error(data.message)
      }

      switch (data.type) {
        case 'heatmap':
          this.createHeatmap(heatmap, data)
          break
        case 'custom_icon':
          this.createHeatmapWithIcons(heatmap, data)
          this.heatmapLegend.removeHeatmap({ id: heatmap.id })
          break
        case 'hex':
          this.createHexGrid(heatmap, data)
          break
      }
    },
    createHeatmapWithIcons (heatmap, data) {
      const icons = data.icons.map(icon => {
        const marker =  L.marker(icon.coordinates, {
          icon: L.divIcon({
            className: icon.icon + '-marker',
            iconSize: [24, 24],
            iconAnchor: [12, 12]
          }),
          draggable: false,
          keyboard: false,
          specialInfo: icon.specialInfo
        })

        this.updatePopupContent(marker)

        marker.on('popupopen', () => {
          this.updatePopupContent(marker)
          this.map.on('zoomend', this.zoomendWhenPopupOpen(marker), this)
        })

        marker.on('popupclose', () => {
          this.map.off('zoomend', this.zoomendWhenPopupOpen(marker), this)
        })

        return marker
      })

      if (heatmap.group) {
        heatmap.group.clearLayers()
        icons.forEach(icon => {
          heatmap.group.addLayer(icon)
        })
      } else {
        heatmap.group = L.layerGroup(icons)
        heatmap.group.addTo(this.map)
      }
    },
    createHeatmap (heatmap, data) {
      if (heatmap.canvas) {
        heatmap.canvas.setLatLngs(data.coordinates)
      } else {
        heatmap.canvas = L.heatLayer(data.coordinates, {
          radius: data.radius,
          blur: data.radius,
          max: data.avg,
          gradient: {
            0.4: '#69F0AE',
            0.6: 'cyan',
            0.7: 'lime',
            0.8: 'yellow',
            1.0: 'red'
          }
        })
        heatmap.canvas.addTo(this.map)
      }

      this.heatmapLegend.addHeatmap({
        id: heatmap.id,
        type: 'heatmap',
        name: heatmap.name,
        max: data.max,
        avg: data.avg,
        min: 1
      })
    },
    createHexGrid (heatmap, data) {
      if (heatmap.canvas) {
        heatmap.canvas.clearLayers()
        heatmap.canvas.addData(data.geoJSON)
      } else {
        heatmap.canvas = L.geoJson(data.geoJSON, {
          style: feature => ({
            color: "#666",
            weight: 1,
            fillColor: feature.properties.fillColor,
            fillOpacity: 0.5
          }),
          onEachFeature: (feature, layer) => {
            layer.bindPopup(feature.properties.popup)
          }
        })
        heatmap.canvas.addTo(this.map)
      }

      this.heatmapLegend.addHeatmap({
        id: heatmap.id,
        type: 'hex',
        name: heatmap.name,
        legend: data.legend
      })
    },
    zoomendWhenPopupOpen (marker) {
      return () => this.updatePopupContent(marker)
    },
    updatePopupContent (marker) {
      const markers = []
      const markerLatLng = marker.getLatLng()
      const [height, width] = marker.getIcon().options.iconSize

      const markerPoint = this.map.latLngToLayerPoint(markerLatLng)

      const boundsPoints = [
        L.point(markerPoint.x - width / 2, markerPoint.y - height / 2),
        L.point(markerPoint.x + width / 2, markerPoint.y - height / 2),
        L.point(markerPoint.x + width / 2, markerPoint.y + height / 2),
        L.point(markerPoint.x - width / 2, markerPoint.y + height / 2)
      ]

      const bounds = boundsPoints.map(point => this.map.layerPointToLatLng(point))

      this.map.eachLayer(layer => {
        if (layer instanceof L.Marker && inside(layer.getLatLng(), bounds)) {
          markers.push(layer)
        }
      })

      this.setPopupWrapper(marker, markers.map(marker => marker.options.specialInfo))
    }
  }
}
</script>